import { useTranslation } from 'react-i18next';
import React from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { TableCell, TableRow } from '../../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { 
  ChevronUp, 
  ChevronDown, 
  Edit, 
  Trash2, 
  MoreHorizontal,
  GripVertical,
  Check,
  X,
  Copy,
  CopyPlus
} from 'lucide-react';
import { format, isWeekend } from 'date-fns';

const EmployeeRow = ({
  employee,
  employees,
  dates,
  index,
  isDragging,
  isDragOver,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  copiedData,
  defaultWorkHours,
  defaultTravelHours,
  isHoliday,
  getEmployeeUnavailability,
  getEntry,
  getAvailabilityTypeInfo,
  onMoveUp,
  onMoveDown,
  onEdit,
  onRemove,
  onSetDayToDefault,
  onSetDayToZero,
  onUpdateEntry,
  onCopyDay,
  onPasteDay,
  onCopyWeek,
  onPasteWeek
}) => {
  const { t } = useTranslation();
  const empData = employees.find(e => e.id === employee.id);
  const empName = empData?.name || employee.name || 'Unknown';

  return (
    <TableRow
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, index)}
      onDragEnd={onDragEnd}
      className={`
        ${isDragging ? 'opacity-50 bg-blue-100' : ''}
        ${isDragOver ? 'border-t-2 border-blue-500' : ''}
        transition-all duration-150
      `}
    >
      {/* Employee name cell */}
      <TableCell className="sticky left-0 bg-white z-10 min-w-[180px]">
        <div className="flex items-center gap-1">
          <div 
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded"
            title={t('auto.ctr.dragToReorder')}
          >
            <GripVertical className="h-4 w-4 text-gray-400" />
          </div>
          <div className="flex flex-col gap-0.5">
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0"
              onClick={onMoveUp}
             data-testid="btn-moveup">
              <ChevronUp className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0"
              onClick={onMoveDown}
             data-testid="btn-movedown">
              <ChevronDown className="h-3 w-3" />
            </Button>
          </div>
          <div className="flex-1">
            <div className="font-medium text-sm">{empName}</div>
            {employee.role && (
              <div className="text-xs text-gray-500">{employee.role}</div>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={onEdit} data-testid="menu-edit">
                <Edit className="h-4 w-4 mr-2" /> {t('auto.ctr.editRole')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={onRemove}
                className="text-red-600"
               data-testid="menu-remove">
                <Trash2 className="h-4 w-4 mr-2" /> {t('auto.ctr.remove')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
      
      {/* Day cells */}
      {dates.map((date) => {
        const entry = getEntry(employee.id, date);
        const { plHoliday, projHoliday, customHoliday } = isHoliday(date);
        const weekend = isWeekend(date);
        const unavailable = getEmployeeUnavailability(employee.id, date);
        const isNonWorking = plHoliday || projHoliday || customHoliday || weekend;
        
        return (
          <TableCell 
            key={date.toISOString()}
            className={`p-1 text-center ${isNonWorking ? 'bg-red-50' : ''} ${unavailable ? 'bg-yellow-50' : ''}`}
          >
            {unavailable ? (
              <Badge 
                className={getAvailabilityTypeInfo(unavailable.availability_type).color + ' text-xs'}
                title={unavailable.reason || getAvailabilityTypeInfo(unavailable.availability_type).label}
              >
                {getAvailabilityTypeInfo(unavailable.availability_type).shortLabel}
              </Badge>
            ) : (
              <div className="space-y-1">
                {/* Quick action buttons */}
                <div className="flex gap-0.5 justify-center">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-5 w-5 p-0 text-xs text-green-600 hover:bg-green-100"
                    onClick={() => onSetDayToDefault(employee.id, date)}
                    title={`Set to default (${defaultWorkHours}W/${defaultTravelHours}T)`}
                  >
                    <Check className="h-3 w-3" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-5 w-5 p-0 text-xs text-red-600 hover:bg-red-100"
                    onClick={() => onSetDayToZero(employee.id, date)}
                    title={t('auto.ctr.setToZero')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                <Input
                  type="number"
                  min="0"
                  max="24"
                  step="0.5"
                  value={entry?.working_hours ?? ''}
                  onChange={(e) => onUpdateEntry(employee.id, date, 'working_hours', parseFloat(e.target.value) || 0)}
                  className="h-7 w-full text-center text-xs"
                  placeholder={String(defaultWorkHours)}
                />
                <Input
                  type="number"
                  min="0"
                  max="24"
                  step="0.5"
                  value={entry?.travel_hours ?? ''}
                  onChange={(e) => onUpdateEntry(employee.id, date, 'travel_hours', parseFloat(e.target.value) || 0)}
                  className="h-7 w-full text-center text-xs bg-blue-50"
                  placeholder={String(defaultTravelHours)}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-5 w-full text-xs">
                      <Copy className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => onCopyDay(employee.id, date)}>
                      <Copy className="h-4 w-4 mr-2" /> {t('auto.ctr.copyDay')}
                    </DropdownMenuItem>
                    {copiedData?.type === 'day' && (
                      <DropdownMenuItem onClick={() => onPasteDay(employee.id, date)}>
                        <CopyPlus className="h-4 w-4 mr-2" /> {t('auto.ctr.pasteDay')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onCopyWeek(employee.id, date)}>
                      <Copy className="h-4 w-4 mr-2" /> {t('auto.ctr.copyWeek')}
                    </DropdownMenuItem>
                    {copiedData?.type === 'week' && (
                      <DropdownMenuItem onClick={() => onPasteWeek(employee.id, date)}>
                        <CopyPlus className="h-4 w-4 mr-2" /> {t('auto.ctr.pasteWeekHere')}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

export default EmployeeRow;
